import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';

const APPLE_STORE_LINK = 'https://apps.apple.com/us/app/opendoor-buy-sell-homes/id1143708068';
const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.opendoor.buyerapp&hl=en';

/** Scanned on desktop; the link lands the phone on its store. Matches the two-QR handoff modal's get-app QR target. */
export const APP_DOWNLOAD_URL = 'https://opendoor.app.link/website';

const APP_STORE_BADGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078078-95093.png?service=cosmos';
const GOOGLE_PLAY_BADGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078081-28903.png?service=cosmos';

export const buildConfirmationAttributedUrl = (
  baseUrl: string,
  content: string,
  addressToken?: string | null,
) => {
  const url = new URL(baseUrl);
  url.searchParams.set('utm_source', 'marketplace_pdp');
  url.searchParams.set('utm_medium', 'tour_booking_confirmation');
  url.searchParams.set('utm_content', content);
  if (addressToken) {
    url.searchParams.set('utm_term', addressToken);
  }
  return url.toString();
};

/**
 * The app-download badges on the booking sheet's confirmation step. Badge
 * assets and store links match the two-QR handoff modal's, but attribution is
 * this surface's own (utm_medium=tour_booking_confirmation), so badge clicks
 * in Journey stay separable from the handoff modal's.
 */
export const AppStoreBadges = ({
  addressToken,
  variant = 'standalone',
}: {
  addressToken?: string | null;
  variant?: 'standalone' | 'inCard';
}) => {
  const badges = (
    [
      {
        href: APPLE_STORE_LINK,
        analyticsName: 'self-tour-booking-app-store-badge-confirmation',
        alt: 'Download on the App Store',
        src: APP_STORE_BADGE_URL,
      },
      {
        href: GOOGLE_PLAY_LINK,
        analyticsName: 'self-tour-booking-google-play-badge-confirmation',
        alt: 'Get it on Google Play',
        src: GOOGLE_PLAY_BADGE_URL,
      },
    ] as const
  ).map((badge) => (
    <Box
      key={badge.alt}
      href={buildConfirmationAttributedUrl(badge.href, 'confirmation', addressToken)}
      external
      analyticsName={badge.analyticsName}
      aria-label={badge.alt}
      trackImpression
      className={variant === 'standalone' ? 'flex-1 md:flex-none' : 'w-31'}
    >
      <Image
        src={badge.src}
        alt={badge.alt}
        className={
          variant === 'standalone'
            ? 'h-11 w-full object-contain md:w-35'
            : 'h-10 w-full object-contain object-left'
        }
      />
    </Box>
  ));
  return variant === 'standalone' ? (
    <Row className="w-full gap-1.5 pt-1">{badges}</Row>
  ) : (
    <Col className="gap-3">{badges}</Col>
  );
};
